import { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';
import { withReactQuery } from '@/react-tools/withReactQuery';

import { openModal } from '@@/modals/open-modal';

import { PolicyType } from '../types';
import { DetachedGroupInfo } from '../queries/usePolicyConflicts';

import {
  PolicyPreviewModal,
  computeDetachedGroups,
} from './PolicyPreviewModal/PolicyPreviewModal';

interface ConfirmPolicyOptions {
  /** ID of the policy being edited (null for new policies) */
  policyId: number | null;
  /** Name of the policy */
  policyName: string;
  /** Type of policy */
  policyType: PolicyType;
  /** IDs of environment groups selected */
  environmentGroupIds: Array<number>;
  /** IDs of environment groups originally assigned (for edit mode) */
  originalEnvironmentGroupIds?: Array<number>;
  /** All available environment groups (for computing detached groups) */
  groups: Array<EnvironmentGroup>;
}

/**
 * Opens a preview modal showing the impact of applying a policy.
 * The modal fetches conflict information internally using React Query.
 * Returns true if user confirms, false if they cancel.
 *
 * @note If the conflict fetch fails, a warning confirmation modal is shown to
 * the user, and this function returns the user's decision (confirm or cancel).
 */
export async function confirmPolicyWithPreview(
  options: ConfirmPolicyOptions
): Promise<boolean> {
  const {
    policyId,
    policyName,
    policyType,
    environmentGroupIds,
    originalEnvironmentGroupIds = [],
    groups,
  } = options;

  // Compute detached groups (groups being removed from the policy)
  const detachedGroups = computeDetachedGroups(
    originalEnvironmentGroupIds,
    environmentGroupIds,
    groups
  );

  // Early return if no groups selected AND no groups being detached
  if (environmentGroupIds.length === 0 && detachedGroups.length === 0) {
    return true;
  }

  // Open the modal - wrapped with withReactQuery to provide QueryClientProvider context
  // since openModal renders in a separate React tree
  const confirmed = await openModal<
    {
      policyName: string;
      policyType: PolicyType;
      policyId: number | null;
      environmentGroupIds: Array<number>;
      detachedGroups: Array<DetachedGroupInfo>;
    },
    boolean
  >(withReactQuery(PolicyPreviewModal), {
    policyName,
    policyType,
    policyId,
    environmentGroupIds,
    detachedGroups,
  });

  return confirmed ?? false;
}
